import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Box, Group, Loader, Text, Title, UnstyledButton } from '@mantine/core';
import { sellersApi } from '@modules/sellers/api/sellers.api';
import { sellerSupportChatApi } from '@modules/seller-support-chat/api/seller-support-chat.api';
import {
  appendSellerSupportMessageToCache,
  CHAT_THREADS_QUERY_KEY,
  CHAT_UNREAD_QUERY_KEY,
  getActiveAdminChatSellerId,
  setActiveAdminChatSellerId,
} from '@modules/seller-support-chat/chat-cache';
import type {
  SellerSupportThreadSummary,
  SellerSupportThreadsResponse,
} from '@modules/seller-support-chat/types';
import { useSellerSupportChatSocket } from '@modules/seller-support-chat/hooks/useSellerSupportChatSocket';
import { SellerSupportChatPanel } from '@modules/seller-support-chat/components/SellerSupportChatPanel';
import { ShopAvatar } from '@modules/seller-support-chat/components/ShopAvatar';
import { Input } from '@shared/ui';
import { useDebounce } from '@shared/hooks';
import '@shared/styles/page.css';
import '@modules/seller-support-chat/components/SellerSupportChatPanel.css';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

/** В списке диалогов: время, если сообщение сегодня, иначе дата. */
function threadListStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`
    : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function sellerLogoUrl(application: Record<string, unknown> | null | undefined) {
  const raw = application?.logoFileUrl;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function upsertThreadInCache(
  prev: SellerSupportThreadsResponse | undefined,
  thread: SellerSupportThreadSummary,
): SellerSupportThreadsResponse {
  const items = prev?.items ?? [];
  const without = items.filter((item) => item.sellerId !== thread.sellerId);
  return {
    items: [thread, ...without],
    total: without.length === items.length ? (prev?.total ?? 0) + 1 : (prev?.total ?? items.length),
  };
}

export default function SellerSupportChatPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const sellerIdFromQuery = Number(searchParams.get('sellerId'));
  const shopNameFromQuery = searchParams.get('shopName')?.trim() || null;
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(
    Number.isFinite(sellerIdFromQuery) && sellerIdFromQuery > 0 ? sellerIdFromQuery : null,
  );
  const [selectedShopName, setSelectedShopName] = useState<string | null>(shopNameFromQuery);
  const [selectedShopLogoUrl, setSelectedShopLogoUrl] = useState<string | null>(null);
  const [sellerSearch, setSellerSearch] = useState('');
  const debouncedSellerSearch = useDebounce(sellerSearch, 300);
  const isSearching = debouncedSellerSearch.trim().length > 0;
  const ensuredSellerIdsRef = useRef<Set<number>>(new Set());

  const threadsQuery = useQuery({
    queryKey: CHAT_THREADS_QUERY_KEY,
    queryFn: () => sellerSupportChatApi.listThreads({ limit: 100 }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const sellersSearchQuery = useQuery({
    queryKey: ['sellers', 'support-chat-search', debouncedSellerSearch],
    queryFn: () => sellersApi.getAll(debouncedSellerSearch),
    enabled: isSearching,
  });

  const threads = threadsQuery.data?.items ?? [];
  const activeSellerId = selectedSellerId;
  const activeThread = useMemo((): SellerSupportThreadSummary | null =>
    threads.find((thread) => thread.sellerId === activeSellerId) ?? null,
    [activeSellerId, threads],
  );

  const ensureThreadMutation = useMutation({
    mutationFn: (sellerId: number) => sellerSupportChatApi.ensureThread(sellerId),
    onSuccess: (thread) => {
      setSelectedShopName(thread.shopName);
      setSelectedShopLogoUrl(thread.shopLogoUrl);
      queryClient.setQueryData<SellerSupportThreadsResponse>(CHAT_THREADS_QUERY_KEY, (prev) =>
        upsertThreadInCache(prev, thread),
      );
    },
  });

  useEffect(() => {
    if (activeSellerId == null || activeSellerId <= 0) return;
    if (activeThread) {
      ensuredSellerIdsRef.current.add(activeSellerId);
      return;
    }
    if (!threadsQuery.isSuccess) return;
    if (ensuredSellerIdsRef.current.has(activeSellerId)) return;
    ensuredSellerIdsRef.current.add(activeSellerId);
    ensureThreadMutation.mutate(activeSellerId);
  }, [activeSellerId, activeThread, threadsQuery.isSuccess, ensureThreadMutation.mutate]);

  const activeShopName =
    activeThread?.shopName ??
    selectedShopName ??
    (activeSellerId ? `Продавец #${activeSellerId}` : null);
  const activeShopLogoUrl = activeThread?.shopLogoUrl ?? selectedShopLogoUrl;

  const openSellerChat = useCallback((sellerId: number, shopName: string, shopLogoUrl?: string | null) => {
    setSelectedSellerId(sellerId);
    setSelectedShopName(shopName);
    setSelectedShopLogoUrl(shopLogoUrl ?? null);
    setSellerSearch('');
    setSearchParams({ sellerId: String(sellerId) }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (!Number.isFinite(sellerIdFromQuery) || sellerIdFromQuery <= 0) return;
    setSelectedSellerId(sellerIdFromQuery);
    if (shopNameFromQuery) {
      setSelectedShopName(shopNameFromQuery);
    }
  }, [sellerIdFromQuery, shopNameFromQuery]);

  useEffect(() => {
    setActiveAdminChatSellerId(activeSellerId);
    return () => setActiveAdminChatSellerId(null);
  }, [activeSellerId]);

  useSellerSupportChatSocket({
    onMessage: ({ sellerId, shopName, message }) => {
      appendSellerSupportMessageToCache(queryClient, sellerId, message, { shopName });
    },
    onUnread: ({ scope, count, sellerId }) => {
      if (scope !== 'admin') return;
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
      if (sellerId != null && getActiveAdminChatSellerId() === sellerId) return;
      queryClient.setQueryData(CHAT_UNREAD_QUERY_KEY, { count });
    },
  });

  const sellerSearchResults = sellersSearchQuery.data ?? [];

  return (
    <div className="page-container seller-support-chat-page">
      <Group className="seller-support-chat-page__header">
        <Title order={2}>Чаты с продавцами</Title>
      </Group>

      <div className="seller-support-inbox">
        <div className="seller-support-inbox__threads">
          <div className="seller-support-inbox__threads-head">
            <Input
              placeholder="Найти продавца по названию…"
              value={sellerSearch}
              onChange={(event) => setSellerSearch(event.currentTarget.value)}
            />
          </div>

          <div className="seller-support-inbox__threads-list">
            {isSearching ? (
              <>
                <Text className="seller-support-inbox__section-label">Продавцы</Text>
                {sellersSearchQuery.isLoading ? (
                  <Group justify="center" py="lg">
                    <Loader size="sm" />
                  </Group>
                ) : null}
                {!sellersSearchQuery.isLoading && sellerSearchResults.length === 0 ? (
                  <Text size="sm" c="dimmed" className="seller-support-inbox__empty">
                    Продавцы не найдены
                  </Text>
                ) : null}
                {sellerSearchResults.map((seller) => {
                  const active = seller.id === activeSellerId;
                  const logoUrl = sellerLogoUrl(seller.application);
                  return (
                    <UnstyledButton
                      key={seller.id}
                      className={`seller-support-inbox__seller-result ${active ? 'seller-support-inbox__seller-result--active' : ''}`}
                      onClick={() => openSellerChat(seller.id, seller.shopName, logoUrl)}
                    >
                      <Group wrap="nowrap" gap="sm" align="flex-start">
                        <ShopAvatar shopName={seller.shopName} logoUrl={logoUrl} size={36} />
                        <div className="seller-support-inbox__thread-body">
                          <Text fw={600} size="sm" lineClamp={1}>
                            {seller.shopName}
                          </Text>
                          <Text size="xs" c="dimmed" mt={2}>
                            Написать продавцу
                          </Text>
                        </div>
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </>
            ) : (
              <>
                <Text className="seller-support-inbox__section-label">Диалоги</Text>
                {threadsQuery.isLoading ? (
                  <Group justify="center" py="xl">
                    <Loader size="sm" />
                  </Group>
                ) : null}
                {!threadsQuery.isLoading && threads.length === 0 ? (
                  <Text size="sm" c="dimmed" className="seller-support-inbox__empty">
                    Пока нет чатов
                  </Text>
                ) : null}
                {threads.map((thread) => {
                  const active = thread.sellerId === activeSellerId;
                  return (
                    <UnstyledButton
                      key={thread.threadId}
                      className={`seller-support-inbox__thread ${active ? 'seller-support-inbox__thread--active' : ''}`}
                      onClick={() => openSellerChat(thread.sellerId, thread.shopName, thread.shopLogoUrl)}
                    >
                      <Group wrap="nowrap" gap="sm" align="flex-start">
                        <ShopAvatar shopName={thread.shopName} logoUrl={thread.shopLogoUrl} size={36} />
                        <div className="seller-support-inbox__thread-body">
                          <div className="seller-support-inbox__thread-top">
                            <span className="seller-support-inbox__thread-title">{thread.shopName}</span>
                            <span className="seller-support-inbox__thread-top-right">
                              {thread.lastMessage ? (
                                <time
                                  className="seller-support-inbox__thread-stamp"
                                  dateTime={thread.lastMessage.createdAt}
                                >
                                  {threadListStamp(thread.lastMessage.createdAt)}
                                </time>
                              ) : null}
                            </span>
                          </div>
                          <div className="seller-support-inbox__thread-preview-row">
                            <span className="seller-support-inbox__thread-preview">
                              {thread.lastMessage?.body?.trim()
                                || (thread.lastMessage?.imageUrls?.length
                                  ? (thread.lastMessage.imageUrls.length === 1
                                    ? 'Фото'
                                    : `Фото (${thread.lastMessage.imageUrls.length})`)
                                  : 'Написать продавцу')}
                            </span>
                            {thread.unreadCount > 0 ? (
                              <span className="seller-support-inbox__unread-count">
                                {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <Box className="seller-support-inbox__chat">
          {activeSellerId ? (
            <SellerSupportChatPanel
              sellerId={activeSellerId}
              shopName={activeShopName}
              shopLogoUrl={activeShopLogoUrl}
            />
          ) : (
            <div className="seller-support-inbox__chat-empty">
              <p className="seller-support-inbox__chat-empty-text">
                Найдите продавца или выберите диалог слева
              </p>
            </div>
          )}
        </Box>
      </div>
    </div>
  );
}
